import React, { useContext } from "react";
import {
  Dialog,
  Grid,
  IconButton,
  Typography,
  Divider,
  Box,
  useTheme,
  Stack,
} from "@mui/material";
import { AppContext } from "../contextProviders/AppContext";
import CloseIcon from "@mui/icons-material/Close";
import { ADDITIONAL_FIELDS_ALL } from "../shared/additional-fields";
import ProductImage from "./ProductImage";
import {
  getFieldValue,
  getFixedLanguage,
  getFixedLanguageSet,
} from "../utilities";

export default function AdditionalFieldsDialog({ onClose, offer }) {
  const { mobileViewPort, language } = useContext(AppContext);
  const fields = offer?.vehicle?.additionalFields;
  //const equipmentInfo = fields?.find((f) => f.key === "equipments")?.value;
  const theme = useTheme();
  return (
    <Dialog
      open={true}
      onClose={onClose}
      // fullScreen={mobileViewPort}
      fullWidth
      fullScreen={mobileViewPort}
      maxWidth={mobileViewPort ? undefined : "lg"}
      sx={{ zIndex: 99999 }}
    >
      <>
        <Stack
          height={"100%"}
          spacing={2}
          sx={{ position: "relative", paddingX: "20px", paddingBottom: "20px" }}
        >
          <Stack
            sx={{ position: "sticky", top: 0 }}
            direction={"column"}
            flex={1}
          >
            <Stack
              direction={"row"}
              justifyContent={"center"}
              flex={1}
              position={"relative"}
              bgcolor={theme.palette.background.default}
              paddingTop={"20px"}
            >
              <Box>
                <Typography
                  variant={mobileViewPort ? "body2" : "h6"}
                  align="center"
                  sx={{
                    color: theme.palette.primary.mainText,
                  }}
                >
                  {offer.vehicle.name.toUpperCase()}
                </Typography>
              </Box>
              <Box position={"absolute"} right={0} top={"10px"}>
                <IconButton aria-label="close" onClick={() => onClose()}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </Stack>
            <Divider variant="middle" />
          </Stack>
          <Box display={"flex"} justifyContent={"center"}>
            <Box
              sx={{
                height: { md: "50%", sm: "100%" },
                width: { md: "50%", sm: "100%" },
              }}
            >
              <ProductImage offer={offer} />
            </Box>
          </Box>
          <Stack direction={mobileViewPort ? "column" : "row"} spacing={2}>
            <Box flex={0.5}>
              <Specifications fields={fields} offer={offer} />
            </Box>
            <Stack flex={0.5} flexDirection={"column"} spacing={1}>
              <EquipmentInfo fields={fields} language={language} />

              <ProductInfo fields={fields} language={language} />
            </Stack>
          </Stack>
        </Stack>
      </>
    </Dialog>
  );
}

function Specifications({ fields, offer }) {
  const { language } = useContext(AppContext);

  const filteredFields = offer?.vehicle?.additionalFields
    ?.reduce((fields, current) => {
      if (current?.key === "equipments" || current?.key === "other") {
        return fields;
      }
      const isFieldActive = ADDITIONAL_FIELDS_ALL.find(
        (a) => a.key === current.key
      );
      if (!isFieldActive) {
        return fields;
      }
      const fieldData = ADDITIONAL_FIELDS_ALL.find(
        (a) => a.key === current.key
      );
      const fixedLanguage = getFixedLanguageSet(language);
      const fieldValue = getFieldValue(current, fixedLanguage);
      if (!fieldValue) {
        return fields;
      }

      return [
        ...fields,
        {
          ...current,
          fieldValue: fieldValue,
          translatedLabel: fieldData?.labelT?.[fixedLanguage],
        },
      ];
    }, [])
    ?.sort((a, b) => {
      if (a?.translatedLabel && b?.translatedLabel) {
        return a?.translatedLabel.localeCompare(b?.translatedLabel);
      }
      return 0;
    });

  return (
    <Grid
      sx={{
        bgcolor: "rgba(0,0,0,0.05)",
        border: "1px solid rgba(0,0,0,0.06)",
        borderRadius: "20px",
      }}
      container
      direction={"row"}
      spacing={1}
      overflow={"hidden"}
      padding={"10px"}
    >
      <>
        <Grid item xs={12}>
          <Typography sx={{ fontSize: "18px" }}>
            <b>Tekniset tiedot</b>
          </Typography>
        </Grid>

        {filteredFields?.map((field, index) => {
          return (
            <Grid
              key={field?.key}
              item
              xs={12}
              sm={6}
              sx={{
                borderBottom: "1px solid rgba(0,0,0,0.07)",
              }}
            >
              <Stack flex={1} spacing={1} direction={"row"}>
                <Box sx={{ flex: 0.6 }}>
                  <Typography>
                    <strong>{field?.translatedLabel}</strong>
                  </Typography>
                </Box>
                <Box sx={{ flex: 0.4 }}>
                  <Typography>{field?.fieldValue}</Typography>
                </Box>
              </Stack>
            </Grid>
          );
        })}
      </>
    </Grid>
  );
}

function EquipmentInfo({ fields }) {
  const { language } = useContext(AppContext);
  const fixedLanguage = getFixedLanguage(language);
  const translatedValue = getTranslatedValue(
    "equipments",
    fixedLanguage,
    fields
  );
  const defaultValue = fields?.find((f) => f.key === "equipments")?.value;
  const fieldValue = translatedValue ?? defaultValue;

  if (!fieldValue) return null;

  return (
    <Box>
      <Typography>{fieldValue}</Typography>
    </Box>
  );
}

function ProductInfo({ fields }) {
  const { language } = useContext(AppContext);

  const fixedLanguage = getFixedLanguage(language);
  const translatedValue = getTranslatedValue("other", fixedLanguage, fields);
  const defaultValue = fields?.find((f) => f.key === "other")?.value;
  const fieldValue = translatedValue ?? defaultValue;

  if (!fieldValue) return null;

  return (
    <>
      <Divider />
      <Box>
        <div
          style={{
            fontSize: 14,
            fontFamily: "Roboto",
            whiteSpace: "pre-line",
            maxWidth: "300px",
          }}
          dangerouslySetInnerHTML={{ __html: fieldValue }}
        />
      </Box>
    </>
  );
}

const getTranslatedValue = (fieldKey, language, fieldData) => {
  const field = fieldData?.find((f) => f.key === fieldKey);
  if (field?.valueT) {
    if (field?.valueT?.[language]) {
      return field?.valueT[language];
    }
  }
  return null;
};
